import { AsyncPipe, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, forwardRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatFormFieldModule, MatHint } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BehaviorSubject, Observable, ObservableInput, Subject, Subscription, catchError, filter, switchMap, tap } from 'rxjs';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCardModule } from '@angular/material/card';
import { BytesPipe } from 'app/utils/bytes.pipe';
import { AllegatiService, UploadFile } from 'app/services/allegati.service';
import { FieldType, FieldTypeConfig, FormlyFieldProps, FormlyModule, }from '@ngx-formly/core';
import { MatDialog } from '@angular/material/dialog';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import * as uuid from 'uuid';
import { environment } from 'environments/environment';

interface UploadProps extends FormlyFieldProps {
    fileList: UploadFile[];
    multiple: boolean;
    accept: string[];
    signed: boolean;
    allegati: UploadFile[];
    allegato$: Subject<UploadFile>;
    allegati$: BehaviorSubject<UploadFile[]>;
    count$: Observable<number>;
    maxsize: number;
    count: number;
    editMode: boolean;
}

@Component({
    selector: 'mat-upload',
    templateUrl: './mat-upload.type.html',
    styleUrls: ['./mat-upload.type.scss'],
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormlyModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatTooltipModule,
        MatIconModule,
        MatFormFieldModule,
        MatProgressBarModule,
        MatCardModule,
        NgTemplateOutlet,
        BytesPipe,
        AsyncPipe
    ],
    providers: [
    {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => MatUploadTypeComponent),
        multi: true,
    },
    ],
})
export class MatUploadTypeComponent extends FieldType<FieldTypeConfig<UploadProps>> implements OnInit, ControlValueAccessor

{
    constructor(
        private _allegatiService: AllegatiService,
        private _changeDetectorRef: ChangeDetectorRef,
        public dialog: MatDialog,
        private _fuseConfirmationService: FuseConfirmationService
    ) {
        super();
    }

    files: UploadFile[] = [];
    newFiles: UploadFile[] = [];

    fileName: string | null = null;
    private _required: boolean;

    accept: string = '';
    multiple: boolean = false;
    label: string = '';
    fieldName: string;
    fieldTitle: string;
    uploadErrors: string[] = [];
    allegati: UploadFile[] = [];
    value: any ;
    fieldSize: number = 0;
    totalSize: number = 0;

    disabled: boolean = false;
    fileUploadError: boolean = false;
    fileUploadSuccess: boolean = false;
    uploadProgress: number = 99;
    uploadSub: Subscription;
    isDragging: boolean = false;
    isDisabled: boolean = false;
    editMode: boolean = false;


    maxFieldSizeAllegati: number; //massima dimensione degli allegati del campo
    maxTotSizeAllegati: number; //massima dimensione complessiva degli allegati dell'istanza

    ngOnInit(): void {
        this.accept = this.field.props.accept?.join(',');
        this.multiple = this.field.props.multiple || false;
        this.label = this.field.props.label;
        this.disabled = this.model.is_new || this.props.disabled;
        this.fieldName = String(this.field.key);
        this.fieldTitle = this.field.props.label;
        this.editMode = this.field.options.formState.editMode || this.field.props.editMode;

        this.maxFieldSizeAllegati = this.field.props.maxsize;
        this._required = this.field.props.required;

        this._allegatiService.allegati$.subscribe(
            (result)=>{
                result.forEach(allegato=>this.totalSize = this.totalSize + allegato.file_size)
                this.allegati = result.filter((item)=>item.field_name==this.field.key);
                this.allegati.forEach(allegato=>this.fieldSize = this.fieldSize + allegato.file_size)
                this.field.props.required = this._required && (this.allegati.length+this.files.length==0);
                this.maxFieldSizeAllegati = (this.field.props.maxsize)*Math.pow(2, 20)
                this.maxTotSizeAllegati = (environment.maxSizeAllegati || 100)*Math.pow(2, 20); //100 MB quando non definito sulla form
            }

        )

    }

    checkErrors(file: File): string {
        const errorMessages: Record<string, string> = {
            singleFile: 'Campo di upload per singolo file. Eliminare il file esistente prima di allegarne uno nuovo.',
            duplicateFile: "Hai già caricato il file",
            bigFile: 'Hai superato la dimensione massima consentita per questo campo.',
            tooLarge: "Hai superato la dimensione massima consentita per gli allegati dell'istanza.",
            invalidType: "Tipo di file non ammesso."
        };

        const isSingleFile = this.allegati.length > 0 && !this.field.props.multiple;
        const exceedsFieldSize = Math.ceil(this.fieldSize + file.size) > this.maxFieldSizeAllegati;
        const exceedsTotalSize = Math.ceil(this.totalSize + file.size) > this.maxTotSizeAllegati;
        const isDuplicate = [...this.files, ...this.allegati].some(item =>
            item.file_name === file.name && item.file_size === file.size && item.mime_type === file.type
        );
        const invalidType = this.field.props.accept && !this.field.props.accept.includes(file.type);

        if (isSingleFile) return errorMessages.singleFile;
        if (isDuplicate) return errorMessages.duplicateFile;
        if (exceedsFieldSize) { this.fieldSize = 0; this.totalSize = 0; this._changeDetectorRef.markForCheck(); return errorMessages.bigFile; }
        if (exceedsTotalSize) { this.fieldSize = 0; this.totalSize = 0; this._changeDetectorRef.markForCheck(); return errorMessages.tooLarge; }
        if (invalidType) return errorMessages.invalidType;

        return '';
    }

    addFiles(files: FileList): void {

        Array.from(files).forEach(file => {
            const error_message = this.checkErrors(file);
            const newFile: UploadFile = {
                file,
                id: uuid.v4(),
                document_id: this.model.id,
                field_name: String(this.field.key),
                field_title: this.field.props.label,
                file_name: file.name,
                file_size: file.size,
                signed: this.field.props.signed ? '1' : '',
                error: !!error_message,
                error_message,
                progress$: new BehaviorSubject<number | null>(null),
            };

            this._allegatiService.uploadFile(newFile);
            this.files.push(newFile);
        });

        this.onChange(this.files);
        this.onTouched();



    }


    onFilesSelected(event: Event): void {
        const input = event.target as HTMLInputElement;

        if (!input.files || input.files.length === 0) {
          return;
        }

        this.addFiles(input.files)




        //this._allegatiService.addFiles(input.files, String(this.field.key), this.field.options.formState.model.id)

    }







    stopUpload(allegato: UploadFile) {

        this._allegatiService.stopUpload(allegato)

    }


    onClick(fileUpload: HTMLInputElement) {
        fileUpload.click();
    }

    cancelUpload() {
        this.uploadSub.unsubscribe();
        this.reset();
    }

    reset(): void {
        this.uploadProgress = null;
        this.uploadSub = null;
    }

    onRemove(event: Event, allegato: UploadFile): void {
        event.preventDefault();
        this.allegati = this.allegati.filter((i) => !(i.file_name == allegato.file_name && allegato.removable));
        this.field.props.count = this.allegati.length;
        this.field.formControl.updateValueAndValidity()
        this._changeDetectorRef.markForCheck()
    }

    onDelete(allegato: UploadFile): void {
        // Open the confirmation dialog
        const confirmation = this._fuseConfirmationService.open({
            title: 'Elimina allegato',
            message:
                'Vuoi eliminare il file allegato già caricato? Questa azione non può essere annullata!',
            actions: {
                confirm: {
                    label: 'Elimina',
                },
                cancel:{
                  label: 'Annulla'
                }

            },
        });

        // Subscribe to the confirmation dialog closed action
        confirmation.afterClosed().pipe(
            filter((response) => response === 'confirmed'),
            switchMap(()=>this._allegatiService.deleteAllegato(allegato))
        ).subscribe((allegato)=>{
            //gestione required, setto il campo vuoto e ripristino il valore iniziale di required

            //se era nei file appena caricati lo tolgo
            if (this.files){
                const index = this.files.findIndex(
                    (item) => item.id === allegato.id
                );
                if (index != -1) {
                    this.files.splice(index, 1);
                }
            }

            if (this.allegati.length==0){
                this.field.formControl.setValue("")
                this.field.props.required=this._required;
            }
            this._changeDetectorRef.markForCheck()
        })

    }


    onDragOver(event: Event): void {
        event.preventDefault();
        this.isDragging = true;
    }

    onDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = false;
        if (event.dataTransfer?.files) {
            this.addFiles(event.dataTransfer.files);
        }
    }



    apriFile(file: UploadFile) {
        this._allegatiService.openFile(file).subscribe()
    }

    scaricaFile(file: UploadFile) {
        this._allegatiService.openFile(file,true).subscribe()
    }

    trackByFn(index: number, allegato: UploadFile): string {
        return allegato.id || String(index);
    }


    private onChange = (value: any[]) => { };
    private onTouched = () => { };

    writeValue(value: UploadFile[]): void {
      this.files = value
    }

    registerOnChange(fn: (value: any[]) => void): void {
        console.log('UUUUUUUUUUUUUUu')
      this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
      this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
      this.isDisabled = isDisabled;
    }



}
